import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Brain, Plus, Pencil, Trash, Play, Lightning, Brain as BrainIcon, Palette, Target, Trophy, Medal, ChartLineUp } from "@phosphor-icons/react";
import ConfirmDialog from "../components/ConfirmDialog";
import ColorLightsPlayer from "../components/neuro/ColorLightsPlayer";
import ColorLightsConfigEditor from "../components/neuro/ColorLightsConfigEditor";
import SimonMemoryPlayer from "../components/neuro/SimonMemoryPlayer";
import StroopPlayer from "../components/neuro/StroopPlayer";
import GoNoGoPlayer from "../components/neuro/GoNoGoPlayer";
import NeuroProgress from "../components/neuro/NeuroProgress";

const QUICK_GAMES = [
  {
    key: "simon",
    title: "Simon de memoria",
    description: "Memorizá una secuencia de colores que crece de a 1 por ronda. Entrena memoria de trabajo y atención sostenida.",
    icon: BrainIcon,
    color: "#32D74B",
  },
  {
    key: "stroop",
    title: "Stroop",
    description: "Tocá el color en el que está pintada la palabra, ignorando lo que dice. Entrena control inhibitorio y atención selectiva.",
    icon: Palette,
    color: "#FF3B30",
  },
  {
    key: "go_nogo",
    title: "Go / No-Go",
    description: "Tocá cuando el círculo sea verde, NO toques si es rojo. Mide reacción y control de impulsos.",
    icon: Target,
    color: "#007AFF",
  },
];

export default function NeurosciencePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [configs, setConfigs] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | configObj
  const [playing, setPlaying] = useState(null);
  const [quickGame, setQuickGame] = useState(null); // 'simon' | 'stroop' | 'go_nogo'
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const [{ data: cfgs }, { data: rk }] = await Promise.all([
        api.get("/neuroscience/configs"),
        api.get("/neuroscience/ranking"),
      ]);
      setConfigs(cfgs || []);
      setRanking(rk || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const reloadRanking = async () => {
    try {
      const { data } = await api.get("/neuroscience/ranking");
      setRanking(data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const deleteConfig = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/neuroscience/configs/${confirmDelete.config_id}`);
      toast.success("Eliminado");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-5 sm:p-8" data-testid="neuroscience-page">
        <div className="mb-6 flex items-center gap-3">
          <Brain size={32} weight="duotone" className="text-primary-app" />
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-muted">Entrenamiento cognitivo</div>
            <h1 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tighter">Neurociencia</h1>
          </div>
        </div>
        <p className="text-muted text-sm mb-8 max-w-2xl">
          Ejercicios y juegos diseñados para entrenar reacción visual, atención y procesamiento neurocognitivo.
          El entrenador configura cada drill; el atleta lo ejecuta cuando le toca.
        </p>

        {/* Juegos directos (sin configuración) */}
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[3px] text-muted mb-3">Juegos rápidos</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {QUICK_GAMES.map((g) => {
              const Icon = g.icon;
              return (
                <div key={g.key} className="card-base p-5 flex flex-col" data-testid={`quick-game-card-${g.key}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 flex items-center justify-center border border-app flex-shrink-0" style={{ backgroundColor: "#0A0A0A" }}>
                      <Icon size={26} weight="duotone" style={{ color: g.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display font-black uppercase text-lg tracking-tight">{g.title}</h3>
                    </div>
                  </div>
                  <p className="text-xs text-muted leading-relaxed flex-1 mb-4">{g.description}</p>
                  <button
                    onClick={() => setQuickGame(g.key)}
                    className="btn-primary !py-2 text-xs flex items-center justify-center gap-2"
                    data-testid={`quick-game-play-${g.key}`}
                  >
                    <Play size={14} weight="fill" /> Jugar
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Catálogo de drills configurables */}
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[3px] text-muted mb-3">Drills configurables por el coach</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="card-base p-5" data-testid="game-card-color_lights">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 flex items-center justify-center border border-app" style={{ backgroundColor: "#0A0A0A" }}>
                  <Lightning size={26} weight="duotone" style={{ color: "#FFD60A" }} />
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-black uppercase text-lg tracking-tight">Luces de colores</h3>
                  <p className="text-xs text-muted mt-1 leading-relaxed">
                    Pantalla que ilumina una secuencia configurable. El entrenador define los colores, duración y target — el atleta entrena reacción visual.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Configuraciones */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="font-display font-black uppercase text-xl tracking-tighter">
            {isAdmin ? "Configuraciones" : "Disponibles para vos"}
          </h2>
          {isAdmin && (
            <button onClick={() => setEditing("new")} className="btn-primary !py-2 !px-3 text-xs flex items-center gap-2" data-testid="new-config-btn">
              <Plus size={14} weight="bold" /> Nueva configuración
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-muted py-12 text-center">Cargando...</div>
        ) : configs.length === 0 ? (
          <div className="card-base p-10 text-center text-muted text-sm">
            {isAdmin
              ? "Todavía no creaste ninguna configuración. Apretá 'Nueva configuración' para armar tu primer drill."
              : "Tu entrenador todavía no te asignó ningún ejercicio cognitivo."}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="configs-list">
            {configs.map((c) => (
              <div key={c.config_id} className="card-base p-4" data-testid={`config-row-${c.config_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-black uppercase tracking-tight text-base truncate">{c.name}</div>
                    {c.description && <div className="text-xs text-muted truncate">{c.description}</div>}
                  </div>
                  <div className="flex flex-wrap gap-1 flex-shrink-0">
                    {(c.sequence || []).slice(0, 6).map((s, i) => (
                      <span key={i} className="w-4 h-4 inline-block border border-[#1a1a1a]" style={{ backgroundColor: s.color }} />
                    ))}
                    {c.sequence?.length > 6 && (
                      <span className="text-[10px] text-muted">+{c.sequence.length - 6}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted mb-3">
                  <span>{c.sequence?.length || 0} pasos</span>
                  <span>·</span>
                  <span>{c.repeats || 1}x repeticiones</span>
                  {c.target_color && (<><span>·</span><span style={{ color: c.target_color }}>Target activo</span></>)}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => setPlaying(c)} className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1" data-testid={`play-${c.config_id}`}>
                    <Play size={12} weight="fill" /> Jugar
                  </button>
                  {isAdmin && (
                    <>
                      <button onClick={() => setEditing(c)} className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1" data-testid={`edit-${c.config_id}`}>
                        <Pencil size={12} weight="bold" /> Editar
                      </button>
                      <button onClick={() => setConfirmDelete(c)} className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1 hover:!border-[#FF3B30] hover:text-[#FF3B30]" data-testid={`delete-${c.config_id}`}>
                        <Trash size={12} weight="bold" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Ranking de atletas */}
        <div className="mt-12" data-testid="ranking-section">
          <div className="flex items-center gap-3 mb-1">
            <Trophy size={22} weight="duotone" className="text-[#FFD60A]" />
            <h2 className="font-display font-black uppercase text-xl tracking-tighter">Ranking</h2>
          </div>
          <p className="text-muted text-xs mb-5 max-w-2xl">
            Top 10 atletas por juego rápido. Se rankea por mejor puntaje y, en caso de empate, por menor tiempo de reacción promedio.
          </p>
          {ranking.length === 0 ? (
            <div className="card-base p-8 text-center text-muted text-sm">Cargando ranking…</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {ranking.map((board) => (
                <div key={board.key} className="card-base p-4" data-testid={`ranking-board-${board.key}`}>
                  <div className="flex items-center justify-between mb-3 pb-3 border-b border-app">
                    <div>
                      <div className="font-display font-black uppercase text-sm tracking-tight">{board.title}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted">{board.metric_label}</div>
                    </div>
                    <Medal size={22} weight="duotone" className="text-[#FFD60A] flex-shrink-0" />
                  </div>
                  {board.entries.length === 0 ? (
                    <div className="text-muted text-xs py-6 text-center">Sin resultados todavía. Sé el primero.</div>
                  ) : (
                    <ol className="space-y-1.5">
                      {board.entries.map((e, idx) => {
                        const isMe = e.user_id === user?.user_id;
                        const podiumColor = idx === 0 ? "#FFD60A" : idx === 1 ? "#C0C0C0" : idx === 2 ? "#CD7F32" : null;
                        return (
                          <li
                            key={e.user_id}
                            className={`flex items-center gap-2 py-1.5 px-2 ${isMe ? "bg-[#FF3B30]/10 border border-[#FF3B30]/40" : "border border-transparent"}`}
                            data-testid={`ranking-${board.key}-row-${idx}`}
                          >
                            <span
                              className="font-display font-black w-6 text-center flex-shrink-0"
                              style={{ color: podiumColor || (isMe ? "#FF3B30" : "#8E8E93") }}
                            >
                              {idx + 1}
                            </span>
                            <span className={`flex-1 truncate text-xs ${isMe ? "font-bold text-white" : ""}`} title={e.user_name}>
                              {e.user_name}{isMe && <span className="text-[9px] text-muted ml-1">(vos)</span>}
                            </span>
                            <span className="font-display font-black text-base text-primary-app">{e.best_score}</span>
                            {board.rt_secondary && e.best_rt_ms != null && (
                              <span className="text-[10px] text-muted w-12 text-right tabular-nums">{e.best_rt_ms}ms</span>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mi evolución */}
        <div className="mt-12" data-testid="neuro-progress-section">
          <div className="flex items-center gap-3 mb-1">
            <ChartLineUp size={22} weight="duotone" className="text-primary-app" />
            <h2 className="font-display font-black uppercase text-xl tracking-tighter">
              {isAdmin ? "Evolución de la app" : "Mi evolución"}
            </h2>
          </div>
          <p className="text-muted text-xs mb-5 max-w-2xl">
            {isAdmin
              ? "Evolución de tus resultados personales en cada juego rápido. Para el detalle por atleta, entrá al perfil del atleta."
              : "Tu progreso en cada juego rápido. Ideal para ver si estás mejorando."}
          </p>
          <NeuroProgress />
        </div>
      </div>

      {editing && (
        <ColorLightsConfigEditor
          existing={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {playing && (
        <ColorLightsPlayer
          config={playing}
          onClose={() => setPlaying(null)}
        />
      )}
      {quickGame === "simon" && (
        <SimonMemoryPlayer onClose={() => { setQuickGame(null); reloadRanking(); }} />
      )}
      {quickGame === "stroop" && (
        <StroopPlayer onClose={() => { setQuickGame(null); reloadRanking(); }} />
      )}
      {quickGame === "go_nogo" && (
        <GoNoGoPlayer onClose={() => { setQuickGame(null); reloadRanking(); }} />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Eliminar configuración"
        body={confirmDelete ? `¿Eliminar "${confirmDelete.name}"? Esta acción no se puede deshacer.` : ""}
        confirmLabel="Eliminar"
        danger
        onConfirm={deleteConfig}
        onCancel={() => setConfirmDelete(null)}
      />
    </Layout>
  );
}
